import React, { useState } from 'react';
import { Alert } from 'reactstrap';
import { buildErrorMessage, handleHttpError } from '@/common/http/errorHandler';
import { ClientError, ServerError, TimeoutError } from '@/common/http/errors';
import type { Customer } from '../../../../../models/companies/kyccs/kycc';
import type { Condition } from '../../../../../models/companies/kyccs/condition';
import { defaultCondition } from '../../../../../models/companies/kyccs/condition';
import * as kyccClient from '../../../../../models/companies/kyccs/kyccClient';
import { Conditions } from './conditions';
import { Checking } from '../checking';
import Results from './results';
import { validate } from '../../../../../models/companies/kyccs/kycc';

const Status = {
  start: 0,
  cheking: 1,
  done: 2,
};

interface Props {
  target: number;
  kyccMonthlyLimit: number;
  kyccCheckedCount: number;
}

export default function ShowView(props: Props) {
  const { target, kyccMonthlyLimit, kyccCheckedCount } = props;

  const [customer, setCustomer] = useState<Customer>({
    id: null,
    companyId: null,
    conditions: [defaultCondition],
    kyccMonthlyLimit: kyccMonthlyLimit,
    kyccCheckedCount: kyccCheckedCount,
    errors: {},
  });
  const [status, setStatus] = useState<number>(Status.start);

  const onAddCondition = () => {
    setCustomer(prev => ({
      ...prev,
      conditions: [...prev.conditions, defaultCondition],
    }));
  };

  const onChangeCondition = (index: number, condition: Condition) => {
    setCustomer(prev => ({
      ...prev,
      conditions: prev.conditions.map((item, i) =>
        i === index ? condition : item,
      ),
    }));
  };

  const onRemoveCondition = (index: number) => {
    setCustomer(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const onStartCheck = () => {
    if (!validate(customer)) {
      setCustomer(prev => ({
        ...prev,
        conditions: [...prev.conditions],
      }));
      return;
    }

    setStatus(Status.cheking);
    kyccClient
      .checkCustomer(target, customer)
      .then(checked => {
        setCustomer(prev => ({ ...prev, ...checked }));
        setStatus(Status.done);
      })
      .catch((error: unknown) => {
        const updatedCustomer = { ...customer };
        if (error instanceof TimeoutError) {
          updatedCustomer.errors.base = [
            '接続がタイムアウトしました。検索結果が多すぎる可能性があります。条件を絞ってお試しください。',
          ];
        } else if (error instanceof ClientError && error.status === 422) {
          // body が空や読めないときは null になるため、オブジェクトのときだけ使う
          updatedCustomer.errors =
            typeof error.body === 'object' && error.body !== null
              ? (error.body as Record<string, string[]>)
              : {};
        } else if (error instanceof ServerError) {
          // message のない 5xx（ALB の 502 など）でも止まらないよう、汎用文言にフォールバックする
          updatedCustomer.errors.base = [buildErrorMessage(error)];
          // 文言は画面内に出しているので、トーストは重ねない
          handleHttpError(error, { toast: false });
        }

        setCustomer(updatedCustomer);
        setStatus(Status.start);
      });
  };

  if (kyccCheckedCount >= kyccMonthlyLimit) {
    return <Alert color="danger">今月の検索上限に到達しています</Alert>;
  } else {
    if (status === Status.start) {
      return (
        <Conditions
          target={target}
          customer={customer}
          onAddCondition={onAddCondition}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
          onStartCheck={onStartCheck}
        />
      );
    } else if (status === Status.cheking) {
      return <Checking />;
    } else if (status === Status.done) {
      return <Results customer={customer} />;
    } else {
      return null;
    }
  }
}
